import { supabase } from './supabase'

/**
 * Initialize Paystack payment
 */
export const initiatePayment = async (organizationId, tierId, billingCycle = 'monthly') => {
  try {
    // Get tier details
    const { data: tier, error: tierError } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('id', tierId)
      .single()

    if (tierError) throw tierError

    // Get ZAR price from metadata if available, otherwise use USD price
    let amount
    if (billingCycle === 'yearly') {
      amount = tier.metadata?.zar_price_yearly || tier.price_yearly || 0
    } else {
      amount = tier.metadata?.zar_price_monthly || tier.price_monthly || 0
    }

    // Create payment transaction record
    const { data: transaction, error: transError } = await supabase
      .from('payment_transactions')
      .insert({
        organization_id: organizationId,
        amount: amount,
        currency: tier.metadata?.zar_price_monthly ? 'ZAR' : (tier.currency || 'NGN'),
        payment_provider: 'paystack',
        status: 'pending',
      })
      .select()
      .single()

    if (transError) {
      console.error('Transaction creation error:', transError)
      throw transError
    }

    // Prepare payment data
    const email = await getOrganizationEmail(organizationId)
    const currency = tier.metadata?.zar_price_monthly ? 'ZAR' : (tier.currency || 'NGN')
    const amountInSmallestUnit = Math.round(amount * 100) // Convert to kobo/cent
    
    const paymentData = {
      email,
      amount: amountInSmallestUnit,
      currency,
      reference: transaction.id,
      metadata: {
        organization_id: organizationId,
        tier_id: tierId,
        billing_cycle: billingCycle,
        transaction_id: transaction.id,
      },
      callback_url: `${window.location.origin}/onboarding?action=verify`,
      // For monthly subscriptions, we'll use Paystack Plans
      use_plan: billingCycle === 'monthly',
      plan_code: tier.metadata?.paystack_plan_code_monthly, // Optional: if you have pre-created plans
    }

    // Call Supabase Edge Function which securely calls Paystack API
    const { data: { session } } = await supabase.auth.getSession()
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
    const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify(paymentData),
    })

    if (!response.ok) throw new Error('Failed to initialize payment')

    const { data: paystackData } = await response.json()

    // Update transaction with Paystack reference
    await supabase
      .from('payment_transactions')
      .update({
        provider_transaction_id: paystackData.reference,
        provider_reference: paystackData.authorization_url,
      })
      .eq('id', transaction.id)

    // Redirect to Paystack payment page
    window.location.href = paystackData.authorization_url

    return { transaction, paystackData }
  } catch (error) {
    console.error('Error initiating payment:', error)
    throw error
  }
}

/**
 * Verify Paystack payment
 */
export const verifyPayment = async (reference) => {
  try {
    // Call Supabase Edge Function which securely verifies with Paystack API
    const { data: { session } } = await supabase.auth.getSession()
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
    const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/verify/${reference}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
    })

    if (!response.ok) throw new Error('Failed to verify payment')

    const { data } = await response.json()

    if (data.status === 'success') {
      // Update transaction
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({
          status: 'success',
          provider_transaction_id: data.reference,
          metadata: data,
        })
        .eq('provider_transaction_id', reference)

      if (updateError) throw updateError

      // Update or create subscription (all payments are subscriptions)
      const metadata = data.metadata
      const subscriptionCode = data.authorization?.subscription_code || data.subscription?.subscription_code || null
      const customerCode = data.authorization?.customer_code || data.customer?.customer_code || null
      
      if (metadata && metadata.organization_id && metadata.tier_id) {
        await updateOrganizationSubscription(
          metadata.organization_id,
          metadata.tier_id,
          metadata.billing_cycle,
          subscriptionCode || reference,
          customerCode
        )

        // Notify master admins of successful upgrade
        try {
          const { notifyMasterAdminsOfSuccessfulUpgrade } = await import('./subscriptionUpgrade')
          await notifyMasterAdminsOfSuccessfulUpgrade(metadata.organization_id, {
            payment_reference: reference,
            new_tier: metadata.tier_id,
            amount: data.amount ? data.amount / 100 : null, // Convert from kobo/cent
            currency: data.currency || 'NGN',
            payment_date: new Date().toISOString(),
            billing_cycle: metadata.billing_cycle,
          })
        } catch (notifyError) {
          console.error('Error notifying master admins:', notifyError)
          // Don't throw - notification failure shouldn't block payment completion
        }
      }

      return { success: true, data }
    }

    return { success: false, data }
  } catch (error) {
    console.error('Error verifying payment:', error)
    throw error
  }
}

/**
 * Update organization subscription
 */
export const updateOrganizationSubscription = async (
  organizationId,
  tierId,
  billingCycle,
  subscriptionCode,
  customerCode = null
) => {
  try {
    // Get tier code from tier_id (organizations.subscription_tier expects a code, not UUID)
    const { data: tier, error: tierError } = await supabase
      .from('subscription_tiers')
      .select('code')
      .eq('id', tierId)
      .single()

    if (tierError) {
      console.error('Error fetching tier code:', tierError)
      throw tierError
    }

    const periodStart = new Date()
    const periodEnd = new Date()
    if (billingCycle === 'yearly') {
      periodEnd.setFullYear(periodEnd.getFullYear() + 1)
    } else {
      periodEnd.setMonth(periodEnd.getMonth() + 1)
    }

    const { data, error } = await supabase
      .from('organization_subscriptions')
      .upsert(
        {
          organization_id: organizationId,
          tier_id: tierId,
          status: 'active',
          billing_cycle: billingCycle,
          current_period_start: periodStart.toISOString(),
          current_period_end: periodEnd.toISOString(),
          cancel_at_period_end: false,
          payment_provider: 'paystack',
          payment_provider_subscription_id: subscriptionCode,
          payment_provider_customer_code: customerCode,
        },
        { onConflict: 'organization_id' }
      )
      .select()
      .single()

    if (error) throw error

    // Update organization tier with tier CODE (not ID)
    const { error: orgUpdateError } = await supabase
      .from('organizations')
      .update({ subscription_tier: tier.code })
      .eq('id', organizationId)

    if (orgUpdateError) {
      console.error('Error updating organization tier:', orgUpdateError)
      throw orgUpdateError
    }

    return data
  } catch (error) {
    console.error('Error updating subscription:', error)
    throw error
  }
}

/**
 * Get organization email for payment
 */
const getOrganizationEmail = async (organizationId) => {
  try {
    // Get current user's email (they're initiating the payment)
    const { data: { user } } = await supabase.auth.getUser()
    if (user?.email) {
      return user.email
    }

    // Fallback: try to get organization admin email from organization_members
    // Note: We can't join auth.users directly, so we'll use the current user's email
    const { data: member } = await supabase
      .from('organization_members')
      .select('user_id')
      .eq('organization_id', organizationId)
      .eq('role', 'admin')
      .limit(1)
      .single()

    // If we have a member, we still can't get their email directly
    // So we'll use the current user's email or a fallback
    return user?.email || 'admin@example.com'
  } catch (error) {
    console.error('Error getting organization email:', error)
    // Fallback to current user's email
    try {
      const { data: { user } } = await supabase.auth.getUser()
      return user?.email || 'admin@example.com'
    } catch {
      return 'admin@example.com'
    }
  }
}

/**
 * Get payment history
 */
export const getPaymentHistory = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching payment history:', error)
    throw error
  }
}

